import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, TrendingUp, TrendingDown, Target, Info } from "lucide-react";
import { toast } from "sonner";

/**
 * AI Rent Estimator widget.
 * Calls POST /api/rent-estimate → { min, mid, max, confidence, reasoning[], comparable_count }.
 */
export default function RentEstimator() {
  const [form, setForm] = useState({
    city: "", locality: "", bhk: 2, area_sqft: 1000,
    property_type: "apartment", furnishing: "semi",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const estimate = async () => {
    if (!form.city) { toast.error("Enter a city to estimate rent"); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/rent-estimate", {
        ...form,
        bhk: +form.bhk,
        area_sqft: +form.area_sqft,
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Estimator failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

  return (
    <section
      className="relative overflow-hidden rounded-3xl bg-white border border-[#EFE3D0] shadow-[0_20px_50px_-25px_rgba(122,30,42,0.18)] p-8 md:p-12"
      data-testid="rent-estimator"
    >
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#C9A24A]/18 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#7A1E2A]/10 blur-3xl" />

      <div className="relative grid md:grid-cols-5 gap-8 items-start">
        <div className="md:col-span-2">
          <span className="inline-flex items-center gap-2 bg-[#7A1E2A]/8 text-[#7A1E2A] px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase mb-4 border border-[#7A1E2A]/20">
            <Sparkles size={14} /> AI Rent Estimator
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#7A1E2A] leading-tight mb-3">
            Is your rent fair?
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Get a data-backed monthly rent range for any Indian home in seconds.
            Our AI compares your inputs against live listings in your city.
          </p>
          {result && (
            <div className="mt-6 flex items-center gap-2 text-xs text-gray-500" data-testid="rent-estimator-source">
              <Info size={12} /> Based on {result.comparable_count} live listing
              {result.comparable_count === 1 ? "" : "s"} · confidence:
              <b className={
                result.confidence === "high" ? "text-emerald-700" :
                result.confidence === "medium" ? "text-amber-700" : "text-gray-700"
              }>{" "}{result.confidence}</b>
            </div>
          )}
        </div>

        <div className="md:col-span-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="est-city" placeholder="City (e.g. Bengaluru)" value={form.city} onChange={(e) => set("city", e.target.value)} />
            <Input data-testid="est-locality" placeholder="Locality (e.g. Indiranagar)" value={form.locality} onChange={(e) => set("locality", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Select value={String(form.bhk)} onValueChange={(v) => set("bhk", +v)}>
              <SelectTrigger data-testid="est-bhk"><SelectValue placeholder="BHK" /></SelectTrigger>
              <SelectContent>
                {[1,2,3,4,5].map((n) => <SelectItem key={n} value={String(n)}>{n} BHK</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="est-area" type="number" inputMode="numeric" placeholder="Area (sqft)" value={form.area_sqft} onChange={(e) => set("area_sqft", e.target.value)} />
            <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
              <SelectTrigger data-testid="est-type"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="apartment">Apartment</SelectItem>
                <SelectItem value="villa">Villa</SelectItem>
                <SelectItem value="house">Independent</SelectItem>
                <SelectItem value="pg">PG</SelectItem>
              </SelectContent>
            </Select>
            <Select value={form.furnishing} onValueChange={(v) => set("furnishing", v)}>
              <SelectTrigger data-testid="est-furnishing"><SelectValue placeholder="Furnishing" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unfurnished">Unfurnished</SelectItem>
                <SelectItem value="semi">Semi</SelectItem>
                <SelectItem value="fully">Fully</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            data-testid="est-submit"
            onClick={estimate}
            disabled={busy}
            className="w-full md:w-auto tlr-gold-btn rounded-full h-12 px-8"
          >
            {busy ? <><Loader2 size={16} className="animate-spin mr-2" /> Estimating…</> : <><Sparkles size={14} className="mr-2" /> Estimate rent</>}
          </Button>

          {result && (
            <div className="mt-4 rounded-2xl border border-[#EFE3D0] bg-[#FFFDF7] p-5 fade-in" data-testid="rent-estimator-result">
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center py-3 rounded-xl bg-white border border-[#EFE3D0]">
                  <div className="flex items-center justify-center gap-1 text-xs text-gray-500 uppercase tracking-widest font-bold mb-1">
                    <TrendingDown size={12} /> Low
                  </div>
                  <div className="font-display text-lg text-[#7A1E2A]" data-testid="est-min">{inr(result.min)}</div>
                </div>
                <div className="text-center py-3 rounded-xl bg-gradient-to-b from-[#7A1E2A] to-[#5C1420] text-white shadow-md border border-[#C9A24A]/30">
                  <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest font-bold mb-1 text-[#C9A24A]">
                    <Target size={12} /> Fair
                  </div>
                  <div className="font-display text-xl" data-testid="est-mid">{inr(result.mid)}<span className="text-xs opacity-80">/mo</span></div>
                </div>
                <div className="text-center py-3 rounded-xl bg-white border border-[#EFE3D0]">
                  <div className="flex items-center justify-center gap-1 text-xs text-gray-500 uppercase tracking-widest font-bold mb-1">
                    <TrendingUp size={12} /> High
                  </div>
                  <div className="font-display text-lg text-[#7A1E2A]" data-testid="est-max">{inr(result.max)}</div>
                </div>
              </div>
              {result.reasoning?.length > 0 && (
                <ul className="space-y-1.5 text-sm text-gray-700" data-testid="est-reasoning">
                  {result.reasoning.map((r, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-[#7A1E2A] mt-1">•</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
